import React, { useEffect, useState } from 'react';
import { useHeaderContext } from '../../../../context/headerContext';
import Button from '../../../../components/Button';
import { useLocation } from 'react-router-dom';
import { useUserContext } from '../../../../context/userContext';
import { useCartContext } from "../../../../context/cartContext";
import { useDrawerContext } from '../../../../context/drawerContext';
import fetchWithRefresh from '../../../../utils/fetchWithRefresh';
import RedCross from "/public/images/icons/redCross.svg?react";
import CloseIcon from "/public/images/icons/close.svg?react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../../utils/LogErrors";

export default function ShopCheckoutSuccess() {
    const { user, logout, updateTokens } = useUserContext();
    const { setTitle, setBackButton } = useHeaderContext();
    const { setOpen, setDrawerContent } = useDrawerContext();
    const { addCart } = useCartContext();
    const [successData, setSuccessData] = useState(null);
    const [upgradeError, setUpgradeError] = useState(false);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    let sessionId = null;
    const paymentIntent = queryParams.get("paymentIntent");
    let purchasedSubscriptionId = queryParams.get("purchasedSubscriptionId");

    if (purchasedSubscriptionId && purchasedSubscriptionId.includes("?id=")) {
        let parts = purchasedSubscriptionId.split("?id=");
        purchasedSubscriptionId = parts[0]; // Value before "?id="
        sessionId = parts[1]; // Value after "?id="
    }
 
    useEffect(() => {
        setTitle("Checkout");
        setBackButton(false);
    }, []);

    useEffect(() => {
        if(paymentIntent){
          registerNewPurchase();
        } else if(sessionId && purchasedSubscriptionId) {
          registerNewSubscription();
        }
    }, [paymentIntent, sessionId])

    const registerNewPurchase = async () => {
        try {
          await Sentry.startSpan({ name: "Register New Purchase", op: "http.request" }, async (span) => {
            const url = `${import.meta.env.VITE_API_URL}Cart/ProductPaymentComplete/?stripePaymentId=${paymentIntent}&userAccountId=${user?.id}`;
            span.setAttribute("http.url", url);
            span.setAttribute("userId", user?.id);
            span.setAttribute("stripePaymentId", paymentIntent);
      
            const response = await fetchWithRefresh(
              url,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${user.token}`,
                },
              },
              logout,
              updateTokens,
              (message) => {
                toast.info(message);
              }
            );
      
            if (response.ok) {
              addCart([]);
            } else {
              throw new Error("Error recording new purchase");
            }
          });
        } catch (error) {
          Sentry.captureException(error, { extra: { userId: user?.id, stripePaymentId: paymentIntent } });
          LogErrors(user, logout, updateTokens, "Error recording new purchase: " + error);
        }
      };

      const registerNewSubscription = async () => {
        try {
          await Sentry.startSpan({ name: "Register New Subscription", op: "http.request" }, async (span) => {
            const url = `${import.meta.env.VITE_API_URL}Cart/SubscriptionPaymentComplete/?stripeSessionId=${sessionId}&userAccountId=${user?.id}&purchasedSubscriptionId=${purchasedSubscriptionId}`;
            span.setAttribute("http.url", url);
            span.setAttribute("userId", user?.id);
            span.setAttribute("stripeSessionId", sessionId);
            span.setAttribute("purchasedSubscriptionId", purchasedSubscriptionId);
      
            const response = await fetchWithRefresh(
              url,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${user.token}`,
                },
              },
              logout,
              updateTokens,
              (message) => {
                toast.info(message);
              }
            );
      
            if (response.ok) {
              const data = await response.json();
              setSuccessData(data[0]);
            } else {
              setUpgradeError(true);
            }
          });
        } catch (error) {
          Sentry.captureException(error, { extra: { userId: user?.id, stripeSessionId: sessionId, purchasedSubscriptionId } });
          LogErrors(user, logout, updateTokens, "Error registering new subscription: " + error);
          setUpgradeError(true);
        } finally {
          setLoading(false);
        }
      };
      

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const supportDrawer = () => {
        setOpen(true);
        setDrawerContent(
            <div className="w-full not-your-band">
                <div className="flex items-center justify-between w-full">
                    <br />
                    <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                        CONTACT SUPPORT
                    </h2>
                    <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
                </div>
                <div className="mt-6 flex flex-col gap-4 justify-stretch">
                    <h3 className="text-[24px]">Please contact us</h3>
                    
                    <div className='flex flex-col'>
                        <p className="text-sportsDarkGrey">Let us know the issue you've ran in to and we will look into it for you.</p>
                        <p className="text-sportsDarkGrey">Our offices are open Monday - Friday between 09:00 - 16:30.</p>
                    </div>

                    <div className="flex flex-col gap-6">
                        <Link href="tel:08009993669">
                            <div className="flex gap-2">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_7364_11237)">
                                    <path d="M5 4H9L11 9L8.5 10.5C9.57096 12.6715 11.3285 14.429 13.5 15.5L15 13L20 15V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21C14.0993 20.763 10.4202 19.1065 7.65683 16.3432C4.8935 13.5798 3.23705 9.90074 3 6C3 5.46957 3.21071 4.96086 3.58579 4.58579C3.96086 4.21071 4.46957 4 5 4Z" stroke="#101820" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </g>
                                <defs>
                                    <clipPath id="clip0_7364_11237">
                                    <rect width="24" height="24" fill="white"/>
                                    </clipPath>
                                </defs>
                                </svg>
                                <span className="underline font-semibold text-sportsBlack">0800 999 3669</span>
                            </div>
                        </Link>

                        <Link href="mailto:hello@sportsguardian.com">
                            <div className="flex gap-2">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_7364_11242)">
                                    <path d="M3 7C3 6.46957 3.21071 5.96086 3.58579 5.58579C3.96086 5.21071 4.46957 5 5 5H19C19.5304 5 20.0391 5.21071 20.4142 5.58579C20.7893 5.96086 21 6.46957 21 7V17C21 17.5304 20.7893 18.0391 20.4142 18.4142C20.0391 18.7893 19.5304 19 19 19H5C4.46957 19 3.96086 18.7893 3.58579 18.4142C3.21071 18.0391 3 17.5304 3 17V7Z" stroke="#101820" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M3 7L12 13L21 7" stroke="#101820" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </g>
                                <defs>
                                    <clipPath id="clip0_7364_11242">
                                    <rect width="24" height="24" fill="white"/>
                                    </clipPath>
                                </defs>
                                </svg>
                                <span className="underline font-semibold text-sportsBlack">hello@sportsguardian.com</span>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='flex flex-col gap-6'>
            {upgradeError ? (
                <>
                    <RedCross width="41" height="40" />
                    <h2>Payment Successful but Upgrade Failed!</h2>
                </>
            ) : (
                <>
                    <svg width="41" height="40" viewBox="0 0 41 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 20C1 9.23045 9.73045 0.5 20.5 0.5C31.2696 0.5 40 9.23045 40 20C40 30.7696 31.2696 39.5 20.5 39.5C9.73045 39.5 1 30.7696 1 20Z" fill="#47B405" stroke="#E3E3E3"/>
                        <path d="M18.0466 27.3902C18.4372 27.7929 19.1013 27.7929 19.4919 27.3902L30.9763 15.5497C31.3669 15.1469 31.3669 14.4623 30.9763 14.0595L29.57 12.6097C29.1794 12.2069 28.5544 12.2069 28.1638 12.6097L18.7888 22.2754L14.3747 17.7647C13.9841 17.362 13.3591 17.362 12.9684 17.7647L11.5622 19.2146C11.1716 19.6173 11.1716 20.302 11.5622 20.7047L18.0466 27.3902Z" fill="white"/>
                    </svg>
                    <h2>Payment Successful!</h2>
                </>
            )}
            {paymentIntent ? (
                <p>Your band(s) have successfully been purchased! Please allow 3-5 days for delivery.</p>
            ) : upgradeError ? (
                <p className="text-[#909090] font-normal">
                    We managed to take payment but failed to upgrade your subscription. Please{' '}
                    <span className="underline text-sportsBlack" onClick={supportDrawer}>
                        contact support
                    </span>{' '}
                    for assistance.
                </p>
            ) : loading ? (
                <p>We're upgrading your subscription...</p>
            ) : successData?.Price && successData?.RenewalDate ? (
                <>
                    <p>Your Subscription has been upgraded, your next payment is:</p>
                    <p>£{successData.Price.toFixed(2)} on {formatDate(successData.RenewalDate)}</p>
                </>
            ) : null}

            <div className='flex flex-col gap-4 pt-6 border-dashed border-t border-[#E3E3E3]'>
                <Button href="/" className="w-full">Go Home</Button>
            </div>
        </div>
    );
};